/* eslint-disable array-callback-return */
import {
  CrudOperators,
  CrudSorting,
  DataProvider,
  HttpError,
} from "@pankod/refine-core";
import axios, { AxiosInstance } from "axios";
import queryString from "query-string";

const generateSort = (sort?: CrudSorting) => {
  let Sorting = ["id"]; // default sorting field
  let Ordering = ["desc"]; // default sorting

  if (sort) {
    Sorting = [];
    Ordering = [];

    sort.map((item) => {
      Sorting.push(item.field);
      Ordering.push(item.order);
    });
  }

  return {
    Sorting,
    Ordering,
  };
};

const mapOperator = (operator: CrudOperators): string => {
  switch (operator) {
    case "ne":
    case "gte":
    case "lte":
      return `_${operator}`;
    case "contains":
      return "_like";
  }

  return ""; // default "eq"
};

type LogicalFilter = {
  field: string;
  operator: Exclude<CrudOperators, "or">;
  value: any;
};
type ConditionalFilter = {
  field: string;
  operator: "or";
  value: LogicalFilter[];
};
type CrudFilter = LogicalFilter | ConditionalFilter;
type Filters = CrudFilter[];

const generateFilter = (filters?: Filters) => {
  const queryFilters: { [key: string]: string } = {};
  if (filters) {
    filters.map(({ field, operator, value }) => {
      const mappedOperator = mapOperator(operator);
      queryFilters[`${field}${mappedOperator}`] = value;
    });
  }

  return queryFilters;
};

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
    };

    return Promise.reject(customError);
  }
);

const CustomDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): DataProvider => ({
  create: async ({ resource, variables }: any) => {
    const url = `${apiUrl}/${resource}`;

    const newPromise = new Promise((rs, rj) => {
      if (resource === "banners" && variables.imgUrl !== undefined) {
        const filess = variables.imgUrl.fileList[0].originFileObj;
        var reader = new FileReader();
        reader.readAsDataURL(filess);
        reader.onload = function () {
          rs(reader.result);
        };
        reader.onerror = function (error) {
          console.log("Error: ", error);
          rj(error);
        };
      }
    });

    const customVariablesImgUrl = newPromise
      .then(async (d) => {
        const dataImgUrl = {
          ...variables,
          imgUrl: {
            ...variables.imgUrl,
            fileList: [{ ...variables.imgUrl.fileList[0], thumbUrl: d }],
          },
        };
        const response =
          resource === "banners"
            ? await httpClient.post(url, dataImgUrl)
            : await httpClient.post(url, variables);

        return { data: response?.data?.data };
      })
      .catch((e) => {
        throw e;
      });

    const response: any =
      resource !== "banners" && (await httpClient.post(url, variables));

    return resource === "banners"
      ? customVariablesImgUrl
      : { data: response?.data?.data };
  },
  createMany: async ({ resource, variables }) => {
    const response = await Promise.all(
      variables.map(async (param) => {
        const { data } = await httpClient.post(`${apiUrl}/${resource}`, param);
        return data;
      })
    );

    return { data: response };
  },
  deleteOne: async ({ resource, id }) => {
    const url = `${apiUrl}/${resource}/${id}`;
    const response = await httpClient.delete(url);

    return { data: response?.data?.data };
  },
  deleteMany: async ({ resource, ids }) => {
    const response = await Promise.all(
      ids.map(async (id) => {
        const { data } = await httpClient.delete(`${apiUrl}/${resource}/${id}`);
        return data;
      })
    );

    return { data: response };
  },
  update: async ({ resource, id, variables }: any) => {
    let resourceApi = resource;
    if (resource === "drafts") {
      resourceApi = "tracks";
    }
    const url = `${apiUrl}/${resourceApi}/${id}`;
    const newPromise = new Promise((rs, rj) => {
      if (resourceApi === "banners" && variables.imgUrl !== undefined) {
        const filess = variables.imgUrl.fileList[0].originFileObj;
        var reader = new FileReader();
        reader.readAsDataURL(filess);
        reader.onload = function () {
          rs(reader.result);
        };
        reader.onerror = function (error) {
          console.log("Error: ", error);
          rj(error);
        };
      }
    });

    const customVariablesImgUrl = newPromise
      .then(async (d) => {
        const dataImgUrl = {
          ...variables,
          imgUrl: {
            ...variables.imgUrl,
            fileList: [{ ...variables.imgUrl.fileList[0], thumbUrl: d }],
          },
        };
        const { data } =
          resourceApi === "banners" && variables.imgUrl !== undefined
            ? await httpClient.put(url, dataImgUrl)
            : await httpClient.put(url, variables);

        return { data };
      })
      .catch((e) => {
        throw e;
      });

    const { data } = await httpClient.put(url, variables);

    return resourceApi === "banners" && variables.imgUrl !== undefined
      ? customVariablesImgUrl
      : { data };
  },
  updateMany: async ({ resource, ids, variables }) => {
    const response = await Promise.all(
      ids.map(async (id) => {
        const { data } = await httpClient.put(
          `${apiUrl}/${resource}/${id}`,
          variables
        );
        return data;
      })
    );

    return { data: response };
  },
  getOne: async ({ resource, id }) => {
    let resourceApi = resource;
    if (resource === "drafts") {
      resourceApi = "tracks";
    }
    const url = `${apiUrl}/${resourceApi}/${id}`;
    const { data } = await httpClient.get(url);

    return { data };
  },
  getMany: async ({ resource, ids }) => {
    const response = await httpClient.get(
      `${apiUrl}/${resource}?${queryString.stringify({ id: ids })}`
    );

    const result = response?.data?.data || [];
    return result;
  },
  getList: async ({ resource, pagination, filters, sort, metaData }) => {
    const searchInResource = resource.split("?search=");

    const newResource = searchInResource[0] || "";

    const searchWithParams = searchInResource[1] || "";

    const url = `${apiUrl}/${newResource}`;
    const current = pagination?.current || "";
    const pageSize = pagination?.pageSize || "";
    let query: {
      [field: string]: number | string;
    } = {
      page: current,
      perPage: pageSize,
    };

    if (sort) {
      (sort || []).forEach((sortField) => {
        query["_sort"] = `${sortField.field}@${sortField.order}`;
      });
    }

    if (filters) {
      (filters || []).forEach((filterField) => {
        // Only add params if they have actual values (not undefined, null, or empty string)
        if (filterField.value.search)
          query["search"] = `${filterField.value.search}`;
        if (filterField.value.labelId)
          query["labelId"] = `${filterField.value.labelId}`;
        if (filterField.value.role) query["role"] = `${filterField.value.role}`;
        if (filterField.value?.contributors)
          query["contributors"] = filterField.value.contributors;
        if (filterField.value?.tags) query["tags"] = filterField.value.tags;
        if (filterField.value?.genres)
          query["genres"] = filterField.value.genres;
        if (filterField.value?.bpm) query["bpm"] = filterField.value.bpm;
        if (filterField.value?.year) query["year"] = filterField.value.year;
        if (filterField.value.type) query["type"] = `${filterField.value.type}`;
        if (filterField.value?.status)
          query["status"] = filterField.value.status;
        if (filterField.value.reason)
          query["reason"] = `${filterField.value.reason}`;
        if (filterField.value.startDate)
          query["startDate"] = `${filterField.value.startDate}`;
        if (filterField.value.endDate)
          query["endDate"] = `${filterField.value.endDate}`;
        if (
          filterField.value.isBlocked !== undefined &&
          filterField.value.isBlocked !== null
        ) {
          query["isBlocked"] = `${filterField.value.isBlocked}`;
        }
        // Advanced order filters
        if (filterField.value.itemName)
          query["itemName"] = `${filterField.value.itemName}`;
        if (filterField.value.userId)
          query["userId"] = `${filterField.value.userId}`;
      });
    }

    if (metaData) {
      (metaData || []).forEach((meta: any) => {
        query[meta.field] = meta.value;
      });
    }

    if (!newResource) {
      return { data: [], total: 1 };
    }

    const { data } = await httpClient.get(
      `${url}?${
        searchWithParams && `search=${searchWithParams}&`
      }${queryString.stringify(query)}`
    );

    // All resources now return the same format: { data: { data: [], totalData: number } }
    const result = data?.data?.data || [];
    const totalData = data?.data?.totalData || 0;

    return { data: result, total: totalData };
  },
  custom: async ({ url, method, filters, sort, payload, query, headers }) => {
    let requestUrl = `${url}?`;

    if (sort) {
      const { Sorting, Ordering } = generateSort(sort);
      const sortQuery = {
        Sort: Sorting.join(","),
        Order: Ordering.join(","),
      };
      requestUrl = `${requestUrl}&${queryString.stringify(sortQuery)}`;
    }

    if (filters) {
      const filterQuery = generateFilter(filters as Filters);
      requestUrl = `${requestUrl}&${queryString.stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${queryString.stringify(query)}`;
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload);
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url);
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;
    return Promise.resolve({ data });
  },
  getApiUrl: () => "",
});

export default CustomDataProvider;
